/**
 * NudgeBell — topbar bell icon surfacing AI-prioritised engagement nudges.
 *
 * Pulls `GET /api/engagement/nudges` (server caps at 5, sorted by
 * severity). Renders:
 *   - Bell icon with a count badge when ≥ 1 visible nudge
 *   - On-click popover listing nudges with quick-action buttons
 *
 * Quick actions are intent-driven verbs:
 *   - mark_done    → PUT /tasks|/commitments|/payments with terminal status
 *   - snooze       → PUT /payments dueDate += params.days  (only payments today)
 *   - navigate     → router push
 *   - dismiss      → local-only hide (localStorage; we don't persist
 *                    dismissals server-side — they re-surface on next login,
 *                    which is intentional for stale/snooze nudges)
 *   - open_daybreak → window.__polkdesqOpenDaybreak() hook
 *   - create_call  → POST /calls minimal stub
 *
 * Refresh strategy:
 *   - 60 s polling while the tab is visible
 *   - Listens to `polkdesq:data-mutated` (broadcast by api.js on any
 *     successful POST/PUT/DELETE) and immediately revalidates — that
 *     way mark-paid → bell badge drops in the same animation frame.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CreditCard, Handshake, Inbox as InboxIcon, Phone,
  ListTodo, Flame, X, ChevronRight, Loader2,
} from 'lucide-react';
import api from '../api';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';
import { addDaysIST } from '../utils/locale';

const POLL_INTERVAL_MS = 60_000;
const DISMISSED_STORAGE_KEY = 'polkdesq:nudge-dismissed';

// Server-driven icon name → lucide component. Keeping this explicit
// (not a `lucide-react` dynamic require) so the icon set is auditable
// and tree-shakeable.
const ICON_MAP = {
  'credit-card': CreditCard,
  handshake: Handshake,
  inbox: InboxIcon,
  phone: Phone,
  'list-todo': ListTodo,
  flame: Flame,
};

const SEVERITY_TONE = {
  high: { dot: 'var(--pd-crimson)', bg: 'var(--pd-crimson-soft)' },
  medium: { dot: 'var(--pd-warning)', bg: 'var(--pd-warning-soft)' },
  low: { dot: 'var(--pd-subtle)', bg: 'transparent' },
};

function loadDismissed() {
  try {
    return new Set(JSON.parse(safeStorage.getString(DISMISSED_STORAGE_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function persistDismissed(set) {
  try {
    safeStorage.setString(DISMISSED_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch (err) {
    logger.warn('NudgeBell: dismissed persist failed', err);
  }
}

function NudgeBell() {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const abortRef = useRef(null);

  const fetchNudges = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    try {
      const res = await api.get('/engagement/nudges', { signal: ctl.signal });
      setNudges(Array.isArray(res?.data?.nudges) ? res.data.nudges : []);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      // 401s and other transient errors — fail closed (no badge),
      // don't toast (this is a passive surface).
      logger.warn('NudgeBell: fetch failed', err?.message || err);
      setNudges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + polling + revalidate-on-mutation.
  useEffect(() => {
    fetchNudges();
    let timer = null;
    const startPolling = () => {
      stopPolling();
      timer = window.setInterval(() => {
        if (!document.hidden) fetchNudges();
      }, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (timer) { window.clearInterval(timer); timer = null; }
    };
    startPolling();

    const onMutated = () => fetchNudges();
    const onVisibility = () => { if (!document.hidden) fetchNudges(); };
    window.addEventListener('polkdesq:data-mutated', onMutated);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopPolling();
      window.removeEventListener('polkdesq:data-mutated', onMutated);
      document.removeEventListener('visibilitychange', onVisibility);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchNudges]);

  const visible = useMemo(
    () => nudges.filter((n) => !dismissed.has(n.id)),
    [nudges, dismissed]
  );
  const badgeCount = visible.length;

  const handleDismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissed(next);
      return next;
    });
  }, []);

  const handleAction = useCallback(async (nudge, action) => {
    setPendingActionId(`${nudge.id}:${action.verb}`);
    try {
      if (action.verb === 'navigate' || (!action.verb && nudge.path)) {
        navigate(action.path || nudge.path || '/');
        setOpen(false);
        return;
      }
      if (action.verb === 'dismiss') {
        handleDismiss(nudge.id);
        return;
      }
      if (action.verb === 'open_daybreak') {
        if (typeof window !== 'undefined' && typeof window.__polkdesqOpenDaybreak === 'function') {
          window.__polkdesqOpenDaybreak();
        } else {
          navigate('/');
        }
        setOpen(false);
        return;
      }
      if (action.verb === 'mark_done') {
        const kind = action.entityKind;
        const id = action.entityId;
        if (kind === 'task') {
          await api.put(`/tasks/${id}`, { status: 'COMPLETED' });
        } else if (kind === 'commitment') {
          await api.put(`/commitments/${id}`, { status: 'DONE' });
        } else if (kind === 'payment') {
          await api.put(`/payments/${id}`, { status: 'PAID' });
        }
        handleDismiss(nudge.id);
        await fetchNudges();
        return;
      }
      if (action.verb === 'delete') {
        const kind = action.entityKind;
        const id = action.entityId;
        if (kind === 'task') await api.delete(`/tasks/${id}`);
        else if (kind === 'commitment') await api.delete(`/commitments/${id}`);
        else if (kind === 'payment') await api.delete(`/payments/${id}`);
        handleDismiss(nudge.id);
        await fetchNudges();
        return;
      }
      if (action.verb === 'snooze') {
        // Only payments today — push dueDate forward by params.days.
        if (action.entityKind === 'payment') {
          const days = action?.params?.days || 3;
          await api.put(`/payments/${action.entityId}`, {
            dueDate: addDaysIST(days),
          });
        }
        handleDismiss(nudge.id);
        await fetchNudges();
        return;
      }
      if (action.verb === 'create_call') {
        const contactName = action?.params?.contactName || '';
        await api.post('/calls', {
          contactName,
          purpose: 'Reconnect',
          status: 'TODAY',
        });
        handleDismiss(nudge.id);
        await fetchNudges();
        return;
      }
    } catch (err) {
      logger.error('NudgeBell action failed', { verb: action.verb, err });
    } finally {
      setPendingActionId(null);
    }
  }, [navigate, handleDismiss, fetchNudges]);

  const togglePopover = () => setOpen((prev) => !prev);

  return (
    <div className="relative" data-testid="nudge-bell-root">
      <button
        type="button"
        data-testid="nudge-bell-button"
        onClick={togglePopover}
        className="pd-icon-button pd-btn-press relative"
        title={badgeCount > 0 ? `${badgeCount} nudge${badgeCount === 1 ? '' : 's'}` : 'No nudges'}
        aria-label={`Engagement nudges (${badgeCount})`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className="w-[16px] h-[16px]" />
        {badgeCount > 0 ? (
          <span
            data-testid="nudge-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
            style={{
              background: 'var(--pd-crimson)',
              color: '#fff',
              boxShadow: '0 0 0 2px var(--pd-canvas)',
            }}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          {/* Click-shield. Same z-stack as Topbar.avatar to avoid layout flicker. */}
          <div
            className="fixed inset-0 z-[200]"
            onClick={() => setOpen(false)}
            data-testid="nudge-bell-shield"
          />
          <div
            role="menu"
            data-testid="nudge-bell-popover"
            className="absolute right-0 top-12 w-[360px] max-w-[92vw] rounded-2xl z-[201] animate-fade-in overflow-hidden"
            style={{
              background: 'var(--pd-popover)',
              border: '1px solid var(--pd-popover-border)',
              boxShadow: 'var(--pd-shadow-3)',
            }}
          >
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--pd-border)' }}>
              <div>
                <div className="text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>Nudges</div>
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>
                  {loading && !visible.length ? 'Loading…' : badgeCount === 0 ? "You're all clear" : `${badgeCount} thing${badgeCount === 1 ? '' : 's'} to tend to`}
                </div>
              </div>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--pd-subtle)' }} /> : null}
            </div>

            <div className="max-h-[60vh] overflow-y-auto" data-testid="nudge-bell-list">
              {visible.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12px]" data-testid="nudge-bell-empty" style={{ color: 'var(--pd-subtle)' }}>
                  Nothing urgent. Go enjoy a deep-work block.
                </div>
              ) : (
                visible.map((n) => {
                  const Icon = ICON_MAP[n.icon] || Bell;
                  const tone = SEVERITY_TONE[n.severity] || SEVERITY_TONE.low;
                  return (
                    <div
                      key={n.id}
                      data-testid={`nudge-${n.kind}`}
                      className="px-4 py-3"
                      style={{ borderBottom: '1px solid var(--pd-border)', background: tone.bg }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'var(--pd-surface-2)', color: tone.dot }}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-semibold truncate" style={{ color: 'var(--pd-text)' }}>{n.title}</div>
                          <div className="text-[11px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>{n.body}</div>

                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {(n.actions || []).map((a, idx) => {
                              const isPending = pendingActionId === `${n.id}:${a.verb}`;
                              return (
                                <button
                                  key={`${n.id}-act-${idx}`}
                                  type="button"
                                  data-testid={`nudge-action-${n.kind}-${a.verb}`}
                                  disabled={isPending || pendingActionId !== null}
                                  onClick={() => handleAction(n, a)}
                                  className="px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors"
                                  style={{
                                    background: 'var(--pd-surface-2)',
                                    color: 'var(--pd-text)',
                                    border: '1px solid var(--pd-border)',
                                    opacity: isPending ? 0.6 : 1,
                                    cursor: isPending ? 'wait' : 'pointer',
                                  }}
                                >
                                  {isPending ? '…' : a.label}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              data-testid={`nudge-open-${n.kind}`}
                              onClick={() => { navigate(n.path || '/'); setOpen(false); }}
                              className="px-2 py-1 rounded-md text-[11px] flex items-center gap-0.5 transition-colors ml-auto"
                              style={{ color: 'var(--pd-subtle)' }}
                              title="Open"
                              aria-label="Open"
                            >
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          data-testid={`nudge-dismiss-${n.kind}`}
                          onClick={() => handleDismiss(n.id)}
                          className="p-1 rounded-md hover:bg-black/[0.04] transition-colors"
                          style={{ color: 'var(--pd-subtle)' }}
                          title="Dismiss"
                          aria-label="Dismiss nudge"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default memo(NudgeBell);
